import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useCartStore } from '../store/cartStore';
import { useStoreSettings } from '../context/StoreSettingsContext';
import { FiShoppingBag, FiSearch, FiMenu, FiX, FiHeart, FiChevronDown } from 'react-icons/fi';
import { useWishlistStore } from '../store/wishlistStore';
import './Header.css';

const DEFAULT_NAV = [
  { label: 'Home',         to: '/'              },
  { label: 'New Arrivals', to: '/new-arrivals' },
  { label: 'Deals',        to: '/deals'         },
  { label: 'Most Selling', to: '/most-selling'  },
  { label: 'About Us',     to: '/about'         },
  { label: 'Contact',      to: '/contact'       },
];

function sendEdit(section) {
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'SECTION_CLICKED', section }, '*');
  }
}

function EditChip({ label }) {
  return <span className="edit-chip">✏ {label}</span>;
}

function Header() {
  const { getCartItemsCount, openDrawer } = useCartStore();
  const { getCount: getWishlistCount }     = useWishlistStore();
  const { settings, editMode, ready }      = useStoreSettings();
  const navigate                           = useNavigate();
  const location                           = useLocation();
  const [searchParams]                     = useSearchParams();
  const [mobileOpen, setMobileOpen]        = useState(false);
  const [searchOpen, setSearchOpen]        = useState(false);
  const [allCategories, setAllCategories]  = useState([]);
  const [openDropdown, setOpenDropdown]    = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/categories`)
      .then(r => r.json())
      .then(data => Array.isArray(data) && setAllCategories(data))
      .catch(() => {});
  }, []);

  // The real top-level category a nav link names, if any
  const matchCategory = (link) => {
    if (!link.to || link.to !== '/products') return null;
    if (link.category_id) return allCategories.find(c => c.id === link.category_id) || null;
    return allCategories.find(c => c.name.toLowerCase() === link.label.toLowerCase() && !c.parent_id) || null;
  };

  // Resolve a nav link's target — if it names a real top-level category, link straight to it
  const getCategoryLink = (link) => {
    if (!link.to || link.to !== '/products') return link.to || '/';
    const match = matchCategory(link);
    return match ? `/products?category_id=${match.id}` : link.to;
  };

  const isNavLinkActive = (link) => {
    const basePath = (link.to || '/').split('?')[0];
    if (basePath !== '/products') return location.pathname === basePath;
    const raw = searchParams.get('category_id') || searchParams.get('cat');
    if (!raw) return false;
    const match = matchCategory(link);
    return match && String(match.id) === raw;
  };

  const { ticker, logo } = settings;
  const navLinks = settings.navLinks ?? DEFAULT_NAV;

  return (
    <>
      {/* ── Announcement Banner — scrolls away ──────── */}
      {ticker?.image_url && ticker?.enabled !== false && (
        <div
          className={editMode ? 'editable-section' : undefined}
          style={{ position: 'relative', lineHeight: 0 }}
          onClick={editMode ? () => sendEdit('ticker') : undefined}
        >
          {editMode && <EditChip label="Edit Banner" />}
          <img src={ticker.image_url} alt="Announcement Banner"
            style={{ width: '100%', height: 68, objectFit: 'cover', display: 'block' }} />
        </div>
      )}

      <header className="header-wrapper">
        <div className="header-top-row">

          <button className="hdr-mobile-toggle" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
            {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>

          <div
            className={`${editMode ? 'editable-section logo-wrap' : 'logo-wrap'} ${ready ? 'is-ready' : ''}`}
            onClick={editMode ? (e) => { e.preventDefault(); sendEdit('logo'); } : undefined}
          >
            {editMode && <EditChip label="Edit Logo" />}
            <Link to="/" className="logo-center" onClick={editMode ? e => e.preventDefault() : undefined}>
              {logo?.image_url
                ? <img src={logo.image_url} alt={logo?.text || 'Store'} style={{ height: 40, maxWidth: 160, objectFit: 'contain', display: 'block' }} />
                : (
                  <span className="logo-block">
                    <svg className="logo-flourish" viewBox="0 0 60 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 14C10 4 16 4 20 10C24 16 30 16 34 8C38 2 46 2 52 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    </svg>
                    <span className="logo-name" style={{ color: logo?.color || 'var(--ink)' }}>
                      {logo?.text || 'Store'}{logo?.dot ? ` ${logo.dot}` : ''}
                    </span>
                    {logo?.tagline && <span className="logo-tagline">{logo.tagline}</span>}
                  </span>
                )
              }
            </Link>
          </div>

          <nav
            className={`header-nav-flat ${ready ? 'is-ready' : ''} ${editMode ? 'editable-section' : ''}`}
            onClick={editMode ? () => sendEdit('nav') : undefined}
          >
            {editMode && <EditChip label="Edit Navigation" />}
            {ready && navLinks.map(link => {
              const cat      = matchCategory(link);
              const children = cat ? allCategories.filter(c => c.parent_id === cat.id) : [];
              return (
                <div
                  key={link.label}
                  className="nav-item-wrap"
                  onMouseEnter={() => children.length > 0 && setOpenDropdown(link.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <NavLink
                    to={getCategoryLink(link)}
                    className={() => `nav-link-flat ${isNavLinkActive(link) ? 'active' : ''}`}
                  >
                    {link.label}
                    {children.length > 0 && <FiChevronDown size={12} className="nav-chevron" />}
                  </NavLink>

                  {children.length > 0 && openDropdown === link.label && (
                    <div className="nav-dropdown">
                      <div className="nav-dropdown-col">
                        <h4>{cat.name}</h4>
                        <div className="nav-dropdown-links">
                          {children.map(ch => (
                            <Link
                              key={ch.id}
                              to={`/products?category_id=${ch.id}`}
                              onClick={() => setOpenDropdown(null)}
                            >
                              {ch.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                      {cat.image_url && (
                        <Link
                          to={`/products?category_id=${cat.id}`}
                          className="nav-dropdown-banner"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <img src={cat.image_url} alt={cat.name} />
                          <span className="nav-dropdown-banner-label">{cat.name}</span>
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="header-actions-min">
            <button className="hdr-icon-btn hdr-search-btn" onClick={() => setSearchOpen(o => !o)} aria-label="Search">
              <FiSearch size={19} />
            </button>
            <Link to="/wishlist" className="hdr-min-btn">
              <span>Wishlist</span>
              <span className="hdr-icon-badge-wrap">
                <FiHeart size={17} />
                {getWishlistCount() > 0 && <span className="hdr-badge">{getWishlistCount()}</span>}
              </span>
            </Link>
            <button className="hdr-min-btn" onClick={() => !editMode && openDrawer()}>
              <FiShoppingBag size={17} />
              <span>({getCartItemsCount()})</span>
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="header-search-expand">
            <form onSubmit={e => {
              e.preventDefault();
              const q = e.target.elements.q.value.trim();
              if (q && !editMode) { navigate(`/products?q=${encodeURIComponent(q)}`); setSearchOpen(false); }
            }}>
              <input autoFocus name="q" type="text" placeholder="Search products…" />
              <button type="submit"><FiSearch size={16} /></button>
            </form>
          </div>
        )}

        {mobileOpen && (
          <nav className="header-nav-mobile">
            {navLinks.map(link => (
              <NavLink
                key={link.label}
                to={getCategoryLink(link)}
                className={() => `nav-link-flat ${isNavLinkActive(link) ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>
    </>
  );
}

export default Header;
